import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useApp } from '@/context/app-context';
import { GOOGLE_CLIENT_ID, SCOPES, APP_VERSION } from '@/lib/constants';

declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token: string }) => void;
          }) => { requestAccessToken: () => void };
        };
      };
    };
  }
}

export function Login() {
  const [isLoading, setIsLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  useEffect(() => {
    if (state.gapiToken) {
      navigate('/');
    }
  }, [state.gapiToken, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleVideoEnd = () => setIsLoading(false);

  const handleGoogleLogin = () => {
    setSigningIn(true);
    setError(null);
    try {
      if (typeof window !== 'undefined' && window.google?.accounts?.oauth2) {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPES,
          callback: (response: { access_token: string }) => {
            if (response.access_token) {
              dispatch({ type: 'SET_GAPI_TOKEN', token: response.access_token });
              navigate('/');
            } else {
              setSigningIn(false);
              setError('Sign-in was cancelled. Please try again.');
            }
          },
        });
        tokenClient.requestAccessToken();
      } else {
        setSigningIn(false);
        setError('Google Sign-In is not available. Please refresh the page and try again.');
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      setSigningIn(false);
      setError('Sign-in failed. Please try again.');
    }
  };

  return (
    <div className="w-full flex flex-col items-center overflow-hidden" style={{ minHeight: '100dvh', backgroundColor: '#18181B' }}>
      <div
        className="relative flex flex-col items-center w-full flex-1"
        style={{
          maxWidth: '430px',
          backgroundColor: '#18181B',
        }}
      >
        {/* Hero logo — always present, blended into background, behind content */}
        <div
          className="absolute left-0 right-0 flex justify-center pointer-events-none"
          style={{ mixBlendMode: 'lighten', top: '0px', zIndex: 0 }}
        >
          <img
            src={`/branding/hero-lockup-clean.png?v=${APP_VERSION}`}
            alt=""
            style={{ width: '320px', height: 'auto' }}
          />
        </div>

        {/* Video splash — covers everything, removed instantly when done */}
        {isLoading && (
          <div
            className="absolute inset-0 flex items-center justify-center overflow-hidden"
            style={{ zIndex: 20, backgroundColor: '#18181B' }}
          >
            <video
              ref={videoRef}
              src={`/branding/login-animation.mp4?v=${APP_VERSION}`}
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              onError={() => setIsLoading(false)}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                mixBlendMode: 'lighten',
                filter: 'contrast(1.08)',
              }}
            />
          </div>
        )}

        {/* Login form content — instant show/hide, never animate opacity (mix-blend-mode: lighten behind it) */}
        <div
          className="w-full flex flex-col items-center flex-1"
          style={{ paddingTop: '16px', position: 'relative', zIndex: 1, visibility: isLoading ? 'hidden' : 'visible' }}
        >
          {/* Spacer for logo area — matches natural image height at 320px width */}
          <div style={{ height: '320px' }} />

          {/* Brand name + tagline */}
          <div className="flex flex-col items-center mb-4">
            <span
              style={{
                color: '#F0F0F0',
                fontSize: '32px',
                fontWeight: 900,
                letterSpacing: '3px',
                lineHeight: 1,
                marginBottom: '6px',
              }}
            >
              LIFTFORGE
            </span>
            <p
              style={{
                color: '#71717A',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
              }}
            >
              Forged, Rep by Rep
            </p>
          </div>

          {/* Stats + CTA + footer */}
          <div className="w-full flex flex-col items-center">
            {/* Stats row */}
            <div className="w-full px-6 mb-4">
              <div
                className="flex justify-around w-full rounded-xl py-4 px-3"
                style={{ backgroundColor: '#27272A', border: '1px solid #3F3F46' }}
              >
                <div className="flex flex-col items-center">
                  <span style={{ color: '#FF6B35', fontSize: '22px', fontWeight: 800 }}>200+</span>
                  <span style={{ color: '#71717A', fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Exercises</span>
                </div>
                <div style={{ width: '1px', backgroundColor: '#3F3F46' }} />
                <div className="flex flex-col items-center">
                  <span style={{ color: '#FF6B35', fontSize: '22px', fontWeight: 800 }}>Your</span>
                  <span style={{ color: '#71717A', fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Google Sheet</span>
                </div>
                <div style={{ width: '1px', backgroundColor: '#3F3F46' }} />
                <div className="flex flex-col items-center">
                  <span style={{ color: '#FF6B35', fontSize: '22px', fontWeight: 800 }}>$0</span>
                  <span style={{ color: '#71717A', fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Forever</span>
                </div>
              </div>
            </div>

            {/* Continue with Google button */}
            <div className="w-full px-6 mb-5">
              <button
                onClick={handleGoogleLogin}
                disabled={signingIn}
                className="w-full rounded-lg flex items-center justify-center gap-3 transition-colors"
                style={{
                  backgroundColor: signingIn ? '#E85A2A' : '#FF6B35',
                  color: '#18181B',
                  padding: '14px 24px',
                  fontSize: '14px',
                  fontWeight: 700,
                  minHeight: '56px',
                  border: 'none',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  opacity: signingIn ? 0.7 : 1,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" fill="#4285F4" />
                  <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853" />
                  <path d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z" fill="#FBBC05" />
                  <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z" fill="#EA4335" />
                </svg>
                {signingIn ? 'Signing in...' : 'Continue with Google'}
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="w-full px-6 mb-3">
                <p style={{ color: '#F87171', fontSize: '13px', textAlign: 'center', lineHeight: '1.5' }}>
                  {error}
                </p>
              </div>
            )}

            {/* How it works collapsible */}
            <div className="w-full px-6 mb-3">
              <button
                onClick={() => setHowItWorksOpen(!howItWorksOpen)}
                className="flex items-center justify-center gap-1 w-full"
                style={{ color: '#71717A', fontSize: '12px', fontWeight: 500, background: 'none', border: 'none' }}
              >
                How it works, privacy & install
                <ChevronDown
                  size={14}
                  style={{
                    transform: howItWorksOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </button>

              {howItWorksOpen && (
                <div
                  className="mt-3 rounded-lg px-4 py-3"
                  style={{ backgroundColor: '#27272A', border: '1px solid #3F3F46' }}
                >
                  <div style={{ color: '#A1A1AA', fontSize: '11px', lineHeight: '1.6' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#F0F0F0' }}>Your data, your Google Sheet.</strong> LiftForge stores all workout data in a Google Spreadsheet on your Drive. Nothing is stored on our servers.
                    </p>
                    <p style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#F0F0F0' }}>Works offline.</strong> Track workouts without internet. Data syncs when you reconnect.
                    </p>
                    <p>
                      <strong style={{ color: '#F0F0F0' }}>Install as app.</strong> On iOS: Safari &rarr; Share &rarr; Add to Home Screen. On Android: Chrome menu &rarr; Install app.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-between w-full px-6 pb-8 pt-2"
              style={{ borderTop: '1px solid #27272A', marginTop: '4px' }}
            >
              <span style={{ color: '#52525b', fontSize: '11px' }}>liftforge.io</span>
              <span style={{ color: '#52525b', fontSize: '11px' }}>v{APP_VERSION}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
